import React from 'react';
import {Pencil, X} from 'lucide-react';
import {
  HEADER_THEME_OPTIONS,
  HeaderThemeId,
  HeaderThemeSelection,
  ImagePosition,
} from '../constants';
import styles from '../Settings.module.css';

interface SettingsAppearanceSectionProps {
  headerTheme: HeaderThemeSelection;
  imagePreview: string;
  imagePosition: ImagePosition;
  imageZoom: number;
  loading: boolean;
  saving: boolean;
  error: string | null;
  onImageUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onOpenImageEditor: () => void;
  onRemoveCustomImage: () => void;
  onThemeChange: (theme: HeaderThemeId) => void;
}

const THEME_LABELS: Record<HeaderThemeId, string> = {
  purple: 'Purple Dream', pink: 'Pink Sunset', blue: 'Ocean Blue', green: 'Fresh Green',
  orange: 'Sunset Orange', dark: 'Dark Night', rainbow: 'Rainbow', gold: 'Golden Hour',
};

const appearanceHeaderIconStyle = {
  '--settings-appearance-icon-url': `url("${process.env.PUBLIC_URL}/icons/appearance.svg")`,
} as React.CSSProperties;

const uploadRoundedIconStyle = {
  '--settings-upload-icon-url': `url("${process.env.PUBLIC_URL}/icons/upload-rounded.svg")`,
} as React.CSSProperties;

const checkRoundedIconStyle = {
  '--settings-check-rounded-icon-url': `url("${process.env.PUBLIC_URL}/icons/check-rounded.svg")`,
} as React.CSSProperties;

const sunIconStyle = {
  '--settings-theme-mode-icon-url': `url("${process.env.PUBLIC_URL}/icons/sun.svg")`,
} as React.CSSProperties;

const moonIconStyle = {
  '--settings-theme-mode-icon-url': `url("${process.env.PUBLIC_URL}/icons/moon.svg")`,
} as React.CSSProperties;

const SettingsAppearanceSection: React.FC<SettingsAppearanceSectionProps> = ({
  headerTheme,
  imagePreview,
  imagePosition,
  imageZoom,
  loading,
  saving,
  error,
  onImageUpload,
  onOpenImageEditor,
  onRemoveCustomImage,
  onThemeChange,
}) => {
  const hasCustomImage = Boolean(imagePreview);
  const controlsDisabled = loading || saving;
  const [themeMode, setThemeMode] = React.useState<'light' | 'dark'>('light');

  return (
    <section className={`${styles.settingsCard} ${styles.appearanceCard}`}>
      <div className={styles.sectionHeadingRow}>
        <span className={styles.sectionIcon}>
          <span className={styles.appearanceHeaderIconAsset} style={appearanceHeaderIconStyle} aria-hidden="true" />
        </span>
        <div>
          <h3 className={styles.settingsCardTitle}>Appearance</h3>
          <p className={styles.settingsCardSubtitle}>Customize how XPSR looks for you.</p>
        </div>
      </div>

      {error ? <p className={styles.inlineErrorText}>{error}</p> : null}

      <div className={styles.appearanceBlock}>
        <span className={styles.appearanceBlockLabel}>Custom Header / Background</span>
        {hasCustomImage ? (
          <div
            className={styles.compactImagePreview}
            style={{
              backgroundImage: `url(${imagePreview})`,
              backgroundSize: `${imageZoom * 100}%`,
              backgroundPosition: `${imagePosition.x}% ${imagePosition.y}%`,
            }}
          >
            <div className={styles.previewActions}>
              <button type="button" disabled={controlsDisabled} onClick={onOpenImageEditor}><Pencil size={16} /> Edit</button>
              <button type="button" disabled={controlsDisabled} onClick={onRemoveCustomImage} aria-label="Remove custom image"><X size={17} /></button>
            </div>
          </div>
        ) : (
          <label htmlFor="header-image-upload" className={`${styles.compactUploadLabel} ${controlsDisabled ? styles.uploadLabelDisabled : ''}`}>
            <input
              id="header-image-upload"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              disabled={controlsDisabled}
              onChange={onImageUpload}
              className={styles.hiddenInput}
            />
            <span
              className={styles.uploadRoundedIcon}
              style={uploadRoundedIconStyle}
              aria-hidden="true"
            />
            <strong>Drag & drop your image here</strong>
            <span>or click to browse</span>
            <small>Max size: 5MB · JPG, PNG, WEBP</small>
          </label>
        )}
      </div>

      <div className={styles.themePresetSection}>
        <span className={styles.appearanceBlockLabel}>Preset Themes</span>
        <div className={styles.themePresetGrid}>
          {HEADER_THEME_OPTIONS.map((theme) => {
            const isSelected = headerTheme === theme.id && !hasCustomImage;
            return (
              <button
                key={theme.id}
                type="button"
                disabled={controlsDisabled}
                onClick={() => onThemeChange(theme.id)}
                className={`${styles.themeOption} ${isSelected ? styles.themeOptionSelected : ''}`}
                aria-pressed={isSelected}
              >
                <span className={styles.themeTile} style={{background: theme.gradient}}>
                  <span>{THEME_LABELS[theme.id]}</span>
                  {isSelected ? (
                    <span
                      className={styles.themeSelectedCheckIcon}
                      style={checkRoundedIconStyle}
                      aria-hidden="true"
                    />
                  ) : null}
                </span>
                <span className={styles.themeRadio}>
                  {isSelected ? (
                    <span
                      className={styles.themeRadioCheckIcon}
                      style={checkRoundedIconStyle}
                      aria-hidden="true"
                    />
                  ) : null}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className={styles.themeModeSection}>
        <span className={styles.appearanceBlockLabel}>Theme Mode</span>
        <div className={styles.themeModeButtons}>
          <button type="button" className={themeMode === 'light' ? styles.themeModeActive : ''} onClick={() => setThemeMode('light')} aria-pressed={themeMode === 'light'}>
            <span className={styles.themeModeIcon} style={sunIconStyle} aria-hidden="true" />
            Light Mode
          </button>
          <button type="button" className={themeMode === 'dark' ? styles.themeModeActive : ''} onClick={() => setThemeMode('dark')} aria-pressed={themeMode === 'dark'}>
            <span className={styles.themeModeIcon} style={moonIconStyle} aria-hidden="true" />
            Dark Mode
          </button>
        </div>
      </div>
    </section>
  );
};

export default React.memo(SettingsAppearanceSection);
